import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type SystemConfig } from "../lib/api";

export type { CookieHealth, SystemConfig } from "../lib/api";

export const useSystemConfig = () =>
  useQuery<SystemConfig>({
    queryKey: ["system-config"],
    queryFn: () => api.system.get(),
    refetchInterval: 60_000,
  });

export const useToggleForceDraftMode = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) => api.system.patch({ forceDraftMode: value }),
    onMutate: async (value) => {
      await qc.cancelQueries({ queryKey: ["system-config"] });
      const previous = qc.getQueryData<SystemConfig>(["system-config"]);
      if (previous) {
        qc.setQueryData<SystemConfig>(["system-config"], {
          ...previous,
          forceDraftMode: value,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["system-config"], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["system-config"] }),
  });
};

export const useToggleScraper = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) => api.system.patch({ scraperEnabled: value }),
    onMutate: async (value) => {
      await qc.cancelQueries({ queryKey: ["system-config"] });
      const previous = qc.getQueryData<SystemConfig>(["system-config"]);
      if (previous) {
        qc.setQueryData<SystemConfig>(["system-config"], {
          ...previous,
          scraperEnabled: value,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["system-config"], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["system-config"] }),
  });
};

export const useToggleOperationalAlerts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: boolean) =>
      api.system.patch({ operationalAlertsEnabled: value }),
    onMutate: async (value) => {
      await qc.cancelQueries({ queryKey: ["system-config"] });
      const previous = qc.getQueryData<SystemConfig>(["system-config"]);
      if (previous) {
        qc.setQueryData<SystemConfig>(["system-config"], {
          ...previous,
          operationalAlertsEnabled: value,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(["system-config"], ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["system-config"] }),
  });
};
